/**
 * 应用运行时工具集
 * 合并 env.ts + constants.ts + fingerprint.ts
 * 提供环境检测、应用常量、浏览器指纹等基础运行时功能
 * 
 * @author AI Assistant
 * @since 2025-01-12
 * @version 2.0.0
 */

// ============= 环境检测 =============

/**
 * 是否为开发环境
 */
export const isDev = import.meta.env.DEV;

/**
 * 是否启用日志
 */
export const enableLog = isDev || import.meta.env.VITE_ENABLE_LOG === 'true';

/**
 * 格式化日志前缀
 */
export const formatPrefix = (module: string): string => {
  return `[TTS-${module}]`;
};

// ============= 应用信息 =============

export const APP_INFO = {
  name: 'TTS Web Vue',
  version: '1.0.0',
  description: '文本转语音Web应用',
  author: 'TTS Team',
  homepage: 'https://tts.example.com',
  repository: 'https://github.com/example/tts-web-vue',
  license: 'MIT'
};



// ============= 存储键名 =============

export const STORAGE_KEYS = {
  // 用户相关
  USER_TOKEN: 'user_token',
  USER_INFO: 'user_info',
  USER_SETTINGS: 'user_settings',
  USER_PREFERENCES: 'user_preferences',
  
  // TTS相关
  TTS_HISTORY: 'tts_history',
  TTS_SETTINGS: 'tts_settings',
  VOICE_SETTINGS: 'voice_settings',
  AUDIO_SETTINGS: 'audio_settings',
  
  // 应用状态
  APP_THEME: 'app_theme',
  APP_LANGUAGE: 'app_language',
  APP_LAYOUT: 'app_layout',
  
  // 缓存相关
  CACHE_VERSION: 'cache_version',
  CACHE_TIMESTAMP: 'cache_timestamp',
  
  // 引导相关
  GUIDE_STATUS: 'guide_status',
  FIRST_VISIT: 'first_visit',
  
  // 其他
  BROWSER_FINGERPRINT: 'browser_fingerprint',
  DEVICE_ID: 'device_id',
  SESSION_ID: 'session_id'
};



// ============= 任务状态映射 =============

export const TASK_STATUS_TEXT_MAP = {
  pending: '等待中',
  processing: '处理中',
  completed: '已完成',
  failed: '失败',
  cancelled: '已取消',
  paused: '已暂停'
};

export const TASK_STATUS_TYPE_MAP = {
  pending: 'info',
  processing: 'warning',
  completed: 'success',
  failed: 'danger',
  cancelled: 'info',
  paused: 'warning'
};

export const TASK_TYPE_TEXT_MAP = {
  single: '单个转换',
  batch: '批量转换',
  api: 'API调用'
};

// ============= 用户层级映射 =============

export const USER_TIER_TEXT_MAP = {
  anonymous: '游客',
  free: '免费用户',
  basic: '基础用户',
  premium: '高级用户',
  enterprise: '企业用户'
};

export const USER_TIER_COLOR_MAP = {
  anonymous: '#909399',
  free: '#67C23A',
  basic: '#409EFF',
  premium: '#E6A23C',
  enterprise: '#F56C6C'
};

// ============= 支付状态映射 =============

export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  CANCELED: 'canceled',
  EXPIRED: 'expired',
  REFUNDED: 'refunded'
} as const;

// ============= API选项配置 =============

export const API_OPTIONS = [
  { label: '免费TTS服务', value: 5 },
  { label: 'Edge TTS', value: 4 },
  { label: 'Azure TTS', value: 1 },
  { label: 'TTS88', value: 3 }
];



// ============= 浏览器指纹生成 =============

let cachedFingerprint: string | null = null;
let isGenerating = false;

/**
 * 生成浏览器指纹（优化版）
 * 优先从内存缓存读取，确保稳定性
 */
export const generateBrowserFingerprint = (): string => {
  // 优先返回内存缓存
  if (cachedFingerprint) {
    return cachedFingerprint;
  }

  // 尝试从localStorage恢复
  const stored = localStorage.getItem(STORAGE_KEYS.BROWSER_FINGERPRINT);
  if (stored) {
    cachedFingerprint = stored;
    return stored;
  }

  // 防止重复生成
  if (isGenerating) {
    // 如果正在生成，返回临时标识
    return 'generating';
  }

  isGenerating = true;

  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Browser fingerprint', 2, 2);
    }

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      !!window.indexedDB,
      typeof(Worker) !== 'undefined',
      navigator.platform,
      navigator.cookieEnabled,
      canvas.toDataURL()
    ].join('|');

    // 简单哈希
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 转换为32位整数
    }

    cachedFingerprint = Math.abs(hash).toString(36);

    // 存储到localStorage
    localStorage.setItem(STORAGE_KEYS.BROWSER_FINGERPRINT, cachedFingerprint);

    return cachedFingerprint;
  } finally {
    isGenerating = false;
  }
};

/**
 * 清除缓存的指纹
 */
export const clearCachedFingerprint = (): void => {
  cachedFingerprint = null;
  localStorage.removeItem(STORAGE_KEYS.BROWSER_FINGERPRINT);
};

/**
 * 获取短指纹（前8位）
 */
export const getShortFingerprint = (): string => {
  const fingerprint = generateBrowserFingerprint();
  return fingerprint.substring(0, 8);
};

/**
 * 从存储中恢复指纹
 */
export const restoreFingerprint = (): string => {
  const stored = localStorage.getItem(STORAGE_KEYS.BROWSER_FINGERPRINT);
  if (stored) {
    cachedFingerprint = stored;
    return stored;
  }
  return generateBrowserFingerprint();
};

// ============= 设备信息 =============

/**
 * 获取设备类型
 */
export const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
    return 'mobile';
  }
  
  if (/tablet|ipad/i.test(userAgent)) {
    return 'tablet';
  }
  
  return 'desktop';
};

/**
 * 检查是否为移动设备
 */
export const isMobile = (): boolean => {
  return getDeviceType() === 'mobile';
};

/**
 * 检查是否为平板设备
 */
export const isTablet = (): boolean => {
  return getDeviceType() === 'tablet';
};

/**
 * 检查是否为桌面设备
 */
export const isDesktop = (): boolean => {
  return getDeviceType() === 'desktop';
};

// ============= 导出工具集合 =============

export const appRuntimeUtils = {
  // 环境检测
  isDev,
  enableLog,
  formatPrefix,
  
  // 应用信息
  APP_INFO,
  
  // 存储和配置
  STORAGE_KEYS,
  
  // 状态映射
  TASK_STATUS_TEXT_MAP,
  TASK_STATUS_TYPE_MAP,
  TASK_TYPE_TEXT_MAP,
  USER_TIER_TEXT_MAP,
  USER_TIER_COLOR_MAP,
  
  // 指纹生成
  generateBrowserFingerprint,
  clearCachedFingerprint,
  getShortFingerprint,
  restoreFingerprint,
  
  // 设备检测
  getDeviceType,
  isMobile,
  isTablet,
  isDesktop
};

export default appRuntimeUtils;
